import { brandLogoUrl } from '@/lib/brandAssets';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_TAGLINE,
  SITE_URL,
  getPublicSiteUrl,
} from '@/lib/site';

export function isNoIndexPath(pathname = '') {
  if (!pathname) return true;
  if (SEO_PAGES[pathname]) return false;
  if (pathname.startsWith('/admin') || pathname.startsWith('/api')) return true;
  if (pathname === '/user' || pathname === '/agent') return true;
  if (pathname.startsWith('/user/') || pathname.startsWith('/agent/')) return true;
  return false;
}

export function absoluteUrl(path = '') {
  const base = getPublicSiteUrl();
  if (!path) return base;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
}

export function formatTitle(pageTitle) {
  if (!pageTitle) return `${SITE_NAME} | ${SITE_TAGLINE}`;
  if (pageTitle.includes(SITE_NAME)) return pageTitle;
  return `${pageTitle} | ${SITE_NAME}`;
}

/** Public marketing & legal pages — indexed; used for DefaultSeo + sitemap */
export const SEO_PAGES = {
  '/': {
    title: `${SITE_NAME} | ${SITE_TAGLINE}`,
    description: DEFAULT_DESCRIPTION,
    path: '/',
  },
  '/investment-sectors': {
    title: `Investment Sectors | ${SITE_NAME}`,
    description:
      'Explore MineMe investment sectors: crypto mining, bullion trade, pharmaceutical R&D, and AI infrastructure operators.',
    path: '/investment-sectors',
  },
  '/terms': {
    title: `Terms of Service | ${SITE_NAME}`,
    description: `Read the ${SITE_NAME} terms of service, investment policies, and platform usage rules.`,
    path: '/terms',
  },
  '/privacy': {
    title: `Privacy Policy | ${SITE_NAME}`,
    description: `Learn how ${SITE_NAME} collects, uses, and protects your personal data.`,
    path: '/privacy',
  },
  '/cookies': {
    title: `Cookie Policy | ${SITE_NAME}`,
    description: `Understand how ${SITE_NAME} uses cookies and similar technologies on the platform.`,
    path: '/cookies',
  },
  '/user/signup': {
    title: `Create Account | ${SITE_NAME}`,
    description: `Sign up for a ${SITE_NAME} investor account and access high-yield investment plans.`,
    path: '/user/signup',
  },
  '/user/register': {
    title: `Register | ${SITE_NAME}`,
    description: `Register your ${SITE_NAME} account to start investing with managed portfolios.`,
    path: '/user/register',
  },
  '/agent/signup': {
    title: `Become an Agent | ${SITE_NAME}`,
    description: `Apply for a ${SITE_NAME} agent account and earn commissions from your referral network.`,
    path: '/agent/signup',
  },
};

export const SITEMAP_PATHS = Object.values(SEO_PAGES).map((p) => p.path);

export function getSeoConfigForPath(pathname = '/') {
  const path = pathname?.split('?')[0] || '/';
  if (path === '/404') {
    return {
      title: `Page Not Found | ${SITE_NAME}`,
      description: 'The page you are looking for does not exist on MineMe.',
      path: '/404',
      noindex: true,
    };
  }
  if (isNoIndexPath(path)) {
    return {
      title: `${SITE_NAME}`,
      description: DEFAULT_DESCRIPTION,
      path,
      noindex: true,
    };
  }
  return SEO_PAGES[path] || SEO_PAGES['/'];
}

export function buildOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: brandLogoUrl('dark'),
      width: 1024,
      height: 1024,
    },
    image: brandLogoUrl('dark'),
    description: DEFAULT_DESCRIPTION,
  };
}

export function buildWebSiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    inLanguage: 'en',
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: brandLogoUrl('dark'),
    },
  };
}

export function buildWebPageJsonLd({ title, description, path }) {
  const url = absoluteUrl(path);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url,
    isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL },
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };
}

export { DEFAULT_OG_IMAGE, DEFAULT_DESCRIPTION };
